import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';

/**
 * Expansion state for a hierarchy table.
 *
 * ─── THE URL CARRIES A MODE, NOT A SET OF IDS ────────────────────────────────────────
 * Five hundred ObjectIds is a ~12 kB URL that `history: 'replace'` would rewrite on every
 * chevron click. Every browser tolerates it; nothing about it is shareable or readable,
 * and it reproduces an INTERACTION LOG rather than a view. A mode is short, canonical, and
 * reproduces what the recipient should see. Per-node toggles live in React state and are
 * reseeded whenever the mode changes.
 *
 * `auto` — relative depths 0 and 1. For a Commissionerate that is the root plus its
 * divisions, which is a useful first screen at any tree size.
 */
export const TREE_EXPANSION_MODES = ['auto', 'all', 'none'] as const;
export type TreeExpansionMode = (typeof TREE_EXPANSION_MODES)[number];

/**
 * Above this many expandable nodes, expand-all expands to a bounded depth instead and
 * says so. A tripwire, not a measurement — the same register as the API's own
 * `MAX_SUBTREE_NODES`.
 */
export const EXPAND_ALL_LIMIT = 300;
export const EXPAND_ALL_FALLBACK_DEPTH = 3;

export type UseTreeExpansionOptions = {
  /** Every key that has children. */
  expandableKeys: readonly string[];
  /** Keys to expand under `auto`. */
  autoKeys: readonly string[];
  /** Keys to expand when expand-all is clamped. */
  boundedKeys: readonly string[];
  urlKey?: string;
};

export type UseTreeExpansion = {
  expandedKeys: string[];
  expandedSet: ReadonlySet<string>;
  onExpand: (expanded: boolean, key: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  /** True when expand-all was clamped, so the toolbar can say what it actually did. */
  truncated: boolean;
};

export const useTreeExpansion = ({
  expandableKeys,
  autoKeys,
  boundedKeys,
  urlKey = 'expand',
}: UseTreeExpansionOptions): UseTreeExpansion => {
  const [mode, setMode] = useQueryState(
    urlKey,
    parseAsStringLiteral(TREE_EXPANSION_MODES).withDefault('auto').withOptions({
      history: 'replace',
      clearOnDefault: true,
    }),
  );

  const clamped = mode === 'all' && expandableKeys.length > EXPAND_ALL_LIMIT;

  const seed = useMemo(() => {
    if (mode === 'none') return [];
    if (mode === 'all') return clamped ? [...boundedKeys] : [...expandableKeys];
    return [...autoKeys];
  }, [mode, clamped, expandableKeys, autoKeys, boundedKeys]);

  /**
   * Per-node overrides on top of the mode's seed, reset when the mode changes.
   *
   * Adjusted DURING RENDER rather than in an effect (React's documented pattern, and the
   * same shape `useServerTable` uses for its search box): an effect would paint one frame
   * with the previous mode's rows still expanded.
   */
  const [seededFor, setSeededFor] = useState(mode);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  if (seededFor !== mode) {
    setSeededFor(mode);
    setOverrides({});
  }

  const expandedKeys = useMemo(() => {
    const keys = new Set(seed);
    for (const [key, expanded] of Object.entries(overrides)) {
      if (expanded) keys.add(key);
      else keys.delete(key);
    }
    return [...keys];
  }, [seed, overrides]);

  const expandedSet = useMemo(() => new Set(expandedKeys), [expandedKeys]);

  const onExpand = useCallback((expanded: boolean, key: string) => {
    setOverrides((previous) => ({ ...previous, [key]: expanded }));
  }, []);

  const expandAll = useCallback(() => void setMode('all'), [setMode]);
  const collapseAll = useCallback(() => void setMode('none'), [setMode]);

  return { expandedKeys, expandedSet, onExpand, expandAll, collapseAll, truncated: clamped };
};
